import {
	Box,
	LinearProgress,
	linearProgressClasses,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CustomTableComponent from "../customTableComponent";

/**
 * Ranking horizontal em formato de TABELA (CustomTableComponent), no mesmo
 * design do dashAtendimentos: cada linha tem uma barra de progresso
 * (LinearProgress determinado) com Tooltip de fundo `paletteCharts.lineCharts`.
 * A cor da barra vem por linha (`row.color`), preservando o semáforo do
 * relatório de reincidência.
 *
 * rows: [{ label, value (0–100), color, labelTooltip?, tooltip? (node) }]
 */
const RankingBarTableComponent = ({
	rows = [],
	valueFormatter = (v) => `${v}%`,
	valueHeader = "Reincidência (%)",
	nameHeader = "Nome",
}) => {
	const theme = useTheme();
	const trackColor = `${
		theme.palette.colors?.gray?.dark || theme.palette.text.secondary
	}2c`;

	const tooltipSlotProps = {
		tooltip: {
			sx: {
				padding: 1,
				backgroundColor: alpha(theme.palette.paletteCharts.lineCharts, 0.9),
				borderRadius: 1,
				color: theme.palette.text.primary,
			},
		},
	};

	const columns = [
		{
			field: "label",
			headerName: nameHeader,
			minWidth: 160,
			renderCell: ({ row }) => (
				<Tooltip
					title={row.labelTooltip || row.label}
					placement="top"
					slotProps={tooltipSlotProps}
				>
					<Typography
						variant="body2"
						noWrap
						sx={{ fontWeight: 500, maxWidth: 240 }}
					>
						{row.label}
					</Typography>
				</Tooltip>
			),
		},
		{
			field: "value",
			headerName: valueHeader,
			type: "number",
			align: "left",
			minWidth: 200,
			renderCell: ({ row }) => (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						width: "100%",
						minWidth: 140,
					}}
				>
					<Tooltip
						placement="top"
						title={row.tooltip ?? valueFormatter(row.value)}
						slotProps={tooltipSlotProps}
					>
						<LinearProgress
							variant="determinate"
							value={Math.max(0, Math.min(Number(row.value) || 0, 100))}
							sx={{
								flex: 1,
								height: 10,
								borderRadius: 5,
								[`&.${linearProgressClasses.colorPrimary}`]: {
									backgroundColor: trackColor,
								},
								[`& .${linearProgressClasses.bar}`]: {
									borderRadius: 5,
									backgroundColor: row.color || theme.palette.primary.main,
								},
							}}
						/>
					</Tooltip>
					<Typography
						variant="body2"
						sx={{ fontWeight: 600, minWidth: 48, textAlign: "right" }}
					>
						{valueFormatter(row.value)}
					</Typography>
				</Box>
			),
		},
	];

	const tableRows = rows.map((row, i) => ({ id: i, ...row }));

	return <CustomTableComponent rows={tableRows} columns={columns} />;
};

export default RankingBarTableComponent;
